import React, { useEffect, useMemo, useState } from "react";
import CircularChart from "../CircularChart";
import { Tabs, Tab, Table, Dropdown, Button } from "react-bootstrap";
import ChartWithIndicators from "../ChartWithIndicators";
import { useNavigate, useSearchParams } from "react-router-dom";

const ReportsTable = (props) => {
  const { data } = props;
  const userType = localStorage.getItem("type");
  const navigate = useNavigate();

  const [callLogsData, setCallLogsData] = useState([]);
  const [webhookData, setWebhookData] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedStatus = searchParams.get("status");

  const [isLoading, setIsLoading] = useState(false);

  // Pagination state
  const [callLogsPage, setCallLogsPage] = useState(1);
  const [webhookPage, setWebhookPage] = useState(1);
  const rowsPerPage = 10;

  useEffect(() => {
    setCallLogsData(data?.call_logs || []);
    setWebhookData(data?.webhook_responses || []);
  }, [data]);

  // Update URL params
  const updateParams = (params) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });
      return next;
    });
  };

  // ---------- STATUS OPTIONS (FROM FULL DATA) ----------
  const statusOptions = useMemo(() => {
    const results = callLogsData
      .map((log) => log?.result)
      .filter(Boolean);

    return [...new Set(results)];
  }, [callLogsData]);

  // ---------- FILTERED CALL LOGS ----------
  const filteredCallLogs = useMemo(() => {
    if (!selectedStatus) return callLogsData;

    return callLogsData.filter(
      (log) => log?.result === selectedStatus
    );
  }, [callLogsData, selectedStatus]);

  // Reset page when filter changes
  useEffect(() => {
    setCallLogsPage(1);
  }, [selectedStatus]);

  // ---------- PAGINATION ----------
  const paginatedCallLogs = filteredCallLogs.slice(
    (callLogsPage - 1) * rowsPerPage,
    callLogsPage * rowsPerPage
  );

  const paginatedWebhookData = webhookData.slice(
    (webhookPage - 1) * rowsPerPage,
    webhookPage * rowsPerPage
  );

  const callLogsTotalPages = Math.ceil(
    filteredCallLogs.length / rowsPerPage
  );
  const webhookTotalPages = Math.ceil(
    webhookData.length / rowsPerPage
  );

  return isLoading ? (
    <div className="d-flex justify-content-center">
      <div className="spinner-border" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  ) : (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "40vh",
        }}
      >
        <ChartWithIndicators data={data?.user_stats} />
      </div>

      <Tabs
        defaultActiveKey="callLogs"
        id="call-logs-tabs"
        className="mb-3"
        justify
      >
        {/* ================= CALL LOGS TAB ================= */}
        <Tab eventKey="callLogs" title="Call Logs">
          <h3>Call Logs</h3>

          <Table striped bordered hover responsive className="bg-white">
            <thead className="bg-light">
              <tr>
                <th>Lead ID</th>
                <th>Contact</th>
                <th>Direction</th>
                <th>Created By</th>
                <th>
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="light"
                      className="w-100 text-start"
                    >
                      Status
                      {selectedStatus ? `: ${selectedStatus}` : ""}
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      {statusOptions.length > 0 ? (
                        statusOptions.map((status) => (
                          <Dropdown.Item
                            key={status}
                            onClick={() =>
                              updateParams({ status })
                            }
                          >
                            {status}
                          </Dropdown.Item>
                        ))
                      ) : (
                        <Dropdown.Item disabled>
                          No Status
                        </Dropdown.Item>
                      )}

                      <Dropdown.Divider />
                      <Dropdown.Item
                        onClick={() =>
                          updateParams({ status: undefined })
                        }
                      >
                        Clear Filter
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </th>

                <th>Hangup Details</th>
                <th>Created At</th>
                {/* <th>Call Recording</th> */}
              </tr>
            </thead>

            <tbody>
              {paginatedCallLogs.length ? (
                paginatedCallLogs.map((log, index) => (
                  <tr key={index}>
                    <td>{log?.lead_id}</td>
                    <td>{log?.customer_number}</td>
                    <td>Outgoing</td>
                    <td>{log?.user?.name}</td>

                    <td>
                      {log?.result ? (
                        <span className="pill pill-blue">
                          {log.result}
                        </span>
                      ) : (
                        <span className="pill pill-grey">
                          No Status
                        </span>
                      )}
                    </td>

                    <td>
                      {log?.hangup_cause || log?.hangup_by ? (
                        <>
                          {log?.hangup_cause && (
                            <span className="pill pill-red">
                              {log.hangup_cause}
                            </span>
                          )}
                          {log?.hangup_by && (
                            <span className="pill pill-blue">
                              {log.hangup_by}
                            </span>
                          )}
                        </>
                      ) : (
                        <span>-</span>
                      )}
                    </td>

                    <td>
                      {log?.created_at
                        ? new Date(
                            log.created_at.$date ??
                              log.created_at
                          ).toLocaleString()
                        : "-"}
                    </td>

                    {/* <td>-</td> */}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center text-muted"
                  >
                    No Data available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {callLogsTotalPages > 1 && (
            <div className="d-flex justify-content-center mt-2">
              <Button
                size="sm"
                disabled={callLogsPage === 1}
                onClick={() =>
                  setCallLogsPage(callLogsPage - 1)
                }
                className="me-2"
              >
                Previous
              </Button>

              <span className="align-self-center">
                Page {callLogsPage} of {callLogsTotalPages}
              </span>

              <Button
                size="sm"
                disabled={
                  callLogsPage === callLogsTotalPages
                }
                onClick={() =>
                  setCallLogsPage(callLogsPage + 1)
                }
                className="ms-2"
              >
                Next
              </Button>
            </div>
          )}
        </Tab>

        {/* ================= WEBHOOK TAB ================= */}
        <Tab eventKey="webhooks" title="Webhook Responses">
          <h3>Webhook Responses</h3>

          <Table striped bordered hover responsive className="bg-white">
            <thead className="bg-light">
              <tr>
                <th>Event</th>
                <th>Customer Number</th>
                <th>Direction</th>
                <th>Status</th>
                <th>Duration (Seconds)</th>
                <th>Timestamp</th>
                <th>Agent</th>
                <th>Recording</th>
              </tr>
            </thead>

            <tbody>
              {paginatedWebhookData.length ? (
                paginatedWebhookData.map((hook, index) => (
                  <tr key={index}>
                    <td>{hook?.action}</td>
                    <td>{hook?.content?.callerIDNumber}</td>
                    <td>{hook?.content?.direction}</td>
                    <td>{hook?.content?.disposition}</td>
                    <td>{hook?.content?.duration}</td>
                    <td>{hook?.content?.createdAt}</td>
                    <td>
                      {hook?.content?.agent?.firstName || "-"}
                    </td>
                    <td>
                      {hook?.content?.recordingFile ? (
                        <a
                          href={hook.content.recordingFile}
                        >
                          Play
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center text-muted"
                  >
                    No Webhook Data available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {webhookTotalPages > 1 && (
            <div className="d-flex justify-content-center mt-2">
              <Button
                size="sm"
                disabled={webhookPage === 1}
                onClick={() =>
                  setWebhookPage(webhookPage - 1)
                }
                className="me-2"
              >
                Previous
              </Button>

              <span className="align-self-center">
                Page {webhookPage} of {webhookTotalPages}
              </span>

              <Button
                size="sm"
                disabled={
                  webhookPage === webhookTotalPages
                }
                onClick={() =>
                  setWebhookPage(webhookPage + 1)
                }
                className="ms-2"
              >
                Next
              </Button>
            </div>
          )}
        </Tab>
      </Tabs>
    </>
  );
};

export default ReportsTable;
